import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const SITE_URL = 'https://pass.tedsvoiceacademy.com';
const COURSE_URL = 'https://voice-training.tedsvoiceacademy.com/courses/master-your-pass-profile/';

// Everything about a product lives in one row here: the menu wording to pick
// from, the code prefix, the short label for the table, and the phrase that
// gets dropped into the copy-and-paste message.
const PRODUCTS = [
  {
    value: 'pass',
    prefix: 'PASS',
    short: 'PASS Profile',
    menu: 'PASS Profile only',
    gets: 'the PASS Profile',
  },
  {
    value: 'bundle',
    prefix: 'BNDL',
    short: 'PASS + Voice-Brand',
    menu: 'PASS Profile + Voice-Brand Alignment',
    gets: 'the PASS Profile and the Voice-Brand Alignment',
  },
  {
    value: 'bundle_course',
    prefix: 'CRSE',
    short: 'PASS + Course',
    menu: 'PASS Profile + Master Your PASS Profile course',
    gets: 'the PASS Profile and the Master Your PASS Profile course',
  },
  {
    value: 'voice_brand',
    prefix: 'VBA',
    short: 'Voice-Brand',
    menu: 'Voice-Brand Alignment only',
    gets: 'the Voice-Brand Alignment',
  },
  {
    value: 'tda',
    prefix: 'TDA',
    short: 'Team Dynamics',
    menu: 'Team Dynamics Alignment only',
    gets: 'the Team Dynamics Alignment',
  },
  {
    value: 'pass_vba_bundle',
    prefix: 'PVBA',
    short: 'PASS + Voice-Brand',
    menu: 'PASS Profile + Voice-Brand Alignment (older version)',
    gets: 'the PASS Profile and the Voice-Brand Alignment',
  },
];

const productInfo = (value) => PRODUCTS.find((p) => p.value === value) || PRODUCTS[0];

// Letters and numbers that stay clear when read aloud or copied by hand.
// I, O, 0 and 1 are left out on purpose.
const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const makeCode = (productType) => {
  let code = productInfo(productType).prefix + '-';
  for (let i = 0; i < 6; i++) {
    code += CODE_CHARS.charAt(Math.floor(Math.random() * CODE_CHARS.length));
  }
  return code;
};

// Only letters, numbers and hyphens survive. A "+" in a code once caused
// customers to be turned away at the login box, so it is filtered out here.
const cleanCode = (raw) => raw.toUpperCase().replace(/[^A-Z0-9-]/g, '');

// The message to send the person. This is the whole point of the page.
const buildInstructions = (code, productType) => {
  const product = productInfo(productType);
  const lines = [
    'Here is your free access to ' + product.gets + '.',
    '',
    '1. Go to ' + SITE_URL + '/login',
    '2. Click "Redeem it here" underneath "Have a coupon code?"',
    '3. Enter this code: ' + code,
    '4. Fill in your full name, your email, and a password of at least 6 characters',
    '5. Tick the box to agree to the Terms, then click "Redeem & Create Account"',
    '',
    'That is everything. Your account opens right away.',
    '',
    'The email you type becomes your login, so pick one you plan to keep.',
  ];
  if (productType === 'bundle_course') {
    lines.push(
      '',
      'For the course itself, go to ' + COURSE_URL,
      'click "Already purchased?", and enter that same email address.'
    );
  }
  return lines.join('\n');
};

function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [typeOwnCode, setTypeOwnCode] = useState(false);
  const [formData, setFormData] = useState({
    product_type: 'pass',
    who: '',
    code: '',
    max_uses: '1',
    expires_at: '',
  });
  const [formError, setFormError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Set after a successful create so the page can show the finished message.
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(null);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    return { 'Authorization': `Bearer ${session.access_token}` };
  };

  const fetchCoupons = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/.netlify/functions/admin-manage-coupons', { headers });
      if (!response.ok) throw new Error('Failed to fetch coupons');
      const data = await response.json();
      setCoupons(data.coupons || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Older browsers, or a page served without https, land here.
      const box = document.createElement('textarea');
      box.value = text;
      box.style.position = 'fixed';
      box.style.opacity = '0';
      document.body.appendChild(box);
      box.select();
      document.execCommand('copy');
      document.body.removeChild(box);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const openForm = () => {
    setFormData({
      product_type: 'pass',
      who: '',
      code: makeCode('pass'),
      max_uses: '1',
      expires_at: '',
    });
    setShowAdvanced(false);
    setTypeOwnCode(false);
    setFormError(null);
    setJustCreated(null);
    setShowForm(true);
  };

  const changeProduct = (value) => {
    setFormData((prev) => ({
      ...prev,
      product_type: value,
      // A fresh code keeps the prefix matching the product, so long as a
      // custom code has not been typed in.
      code: typeOwnCode ? prev.code : makeCode(value),
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);

    const code = cleanCode(formData.code);
    if (code.length < 3) {
      setFormError('Give the code at least 3 letters or numbers.');
      return;
    }

    setCreating(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/.netlify/functions/admin-manage-coupons', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          max_uses: formData.max_uses ? parseInt(formData.max_uses, 10) : null,
          expires_at: formData.expires_at || null,
          note: formData.who || null,
          product_type: formData.product_type,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setFormError(data.error);
      } else {
        setJustCreated({
          code,
          product_type: formData.product_type,
          who: formData.who,
          max_uses: formData.max_uses ? parseInt(formData.max_uses, 10) : null,
        });
        setShowForm(false);
        fetchCoupons();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (coupon) => {
    try {
      const headers = await getAuthHeaders();
      await fetch('/.netlify/functions/admin-manage-coupons', {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: coupon.id, is_active: !coupon.is_active }),
      });
      fetchCoupons();
    } catch (err) {
      alert('Error changing coupon: ' + err.message);
    }
  };

  const handleDelete = async (coupon) => {
    const warning =
      'Delete the code "' + coupon.code + '" for good?\n\n' +
      'Anyone still holding it will be turned away at the login box. ' +
      'To pause it instead and keep the record, choose Turn off.';
    if (!window.confirm(warning)) return;

    try {
      const headers = await getAuthHeaders();
      await fetch('/.netlify/functions/admin-manage-coupons', {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: coupon.id }),
      });
      fetchCoupons();
    } catch (err) {
      alert('Error deleting coupon: ' + err.message);
    }
  };

  const statusOf = (coupon) => {
    if (!coupon.is_active) {
      return { label: 'Turned off', bg: '#F5F5F5', fg: '#757575' };
    }
    if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
      return { label: 'Past its date', bg: '#FFF3E0', fg: '#E65100' };
    }
    if (coupon.max_uses && coupon.current_uses >= coupon.max_uses) {
      return { label: 'Used up', bg: '#E3F2FD', fg: '#1565C0' };
    }
    return { label: 'Ready to use', bg: '#E8F5E9', fg: '#2E7D32' };
  };

  if (loading) return <p style={styles.loading}>Loading coupons...</p>;
  if (error) return <p style={styles.error}>Error: {error}</p>;

  return (
    <div>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Coupon Codes</h1>
          <p style={styles.subtitle}>Give someone free access without a payment</p>
        </div>
        {!showForm && (
          <button onClick={openForm} style={styles.createBtn}>
            + Make a coupon
          </button>
        )}
      </div>

      {/* ---------- The finished message, shown right after a create ---------- */}
      {justCreated && (
        <div style={styles.successCard}>
          <div style={styles.successHeader}>
            <h3 style={styles.successTitle}>Your coupon is ready</h3>
            <button onClick={() => setJustCreated(null)} style={styles.closeBtn}>Close</button>
          </div>

          <div style={styles.bigCodeRow}>
            <span style={styles.bigCode}>{justCreated.code}</span>
            <button
              onClick={() => copyText(justCreated.code, 'code')}
              style={styles.copyBtnSmall}
            >
              {copied === 'code' ? 'Copied' : 'Copy code'}
            </button>
          </div>

          <p style={styles.successSummary}>
            Good for <strong>{productInfo(justCreated.product_type).short}</strong>
            {justCreated.who ? <span> &middot; for <strong>{justCreated.who}</strong></span> : null}
            <span> &middot; </span>
            {justCreated.max_uses === 1
              ? 'one person only'
              : justCreated.max_uses
                ? justCreated.max_uses + ' people'
                : 'open to anyone holding the code'}
          </p>

          <div style={styles.stepTwo}>
            <h4 style={styles.stepTwoTitle}>Send this to them</h4>
            <button
              onClick={() => copyText(buildInstructions(justCreated.code, justCreated.product_type), 'msg')}
              style={styles.copyBtnBig}
            >
              {copied === 'msg' ? 'Copied to your clipboard' : 'Copy the whole message'}
            </button>
            <pre style={styles.messageBox}>
              {buildInstructions(justCreated.code, justCreated.product_type)}
            </pre>
          </div>
        </div>
      )}

      {/* ---------- Two questions, then a button ---------- */}
      {showForm && (
        <div style={styles.formCard}>
          <h3 style={styles.formTitle}>Make a coupon</h3>
          <form onSubmit={handleCreate}>

            <div style={styles.step}>
              <label style={styles.label}>1. Who is this for?</label>
              <input
                style={styles.input}
                type="text"
                value={formData.who}
                onChange={(e) => setFormData({ ...formData, who: e.target.value })}
                placeholder="Their name or email, so you remember later"
                autoFocus
              />
              <p style={styles.hint}>
                This is a label for your own list. The person never sees it, and the
                code works whatever email they sign up with.
              </p>
            </div>

            <div style={styles.step}>
              <label style={styles.label}>2. What do they get?</label>
              <select
                style={styles.input}
                value={formData.product_type}
                onChange={(e) => changeProduct(e.target.value)}
              >
                {PRODUCTS.map((p) => (
                  <option key={p.value} value={p.value}>{p.menu}</option>
                ))}
              </select>
            </div>

            <div style={styles.step}>
              <label style={styles.label}>3. Their code</label>
              {typeOwnCode ? (
                <input
                  style={{ ...styles.input, ...styles.codeInput }}
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: cleanCode(e.target.value) })}
                  placeholder="TAYLORPASS"
                />
              ) : (
                <div style={styles.codeDisplay}>{formData.code}</div>
              )}
              <div style={styles.codeActions}>
                {!typeOwnCode && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, code: makeCode(formData.product_type) })}
                    style={styles.linkBtn}
                  >
                    Give me a different one
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setTypeOwnCode(!typeOwnCode)}
                  style={styles.linkBtn}
                >
                  {typeOwnCode ? 'Go back to a made-up code' : 'Type my own code'}
                </button>
              </div>
              <p style={styles.hint}>
                Letters, numbers and hyphens are kept. Anything else drops away as you
                type, which keeps the code working at the login box.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={styles.advancedToggle}
            >
              {showAdvanced ? 'Hide the extra settings' : 'Extra settings (most of the time, skip these)'}
            </button>

            {showAdvanced && (
              <div style={styles.advancedBox}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>How many people can use it?</label>
                    <input
                      style={styles.input}
                      type="number"
                      min="1"
                      value={formData.max_uses}
                      onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                      placeholder="Leave empty for anyone"
                    />
                    <p style={styles.hint}>
                      1 keeps it to a single person. Empty opens it to everyone holding the code.
                    </p>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Last day it works</label>
                    <input
                      style={styles.input}
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    />
                    <p style={styles.hint}>Empty means it keeps working for good.</p>
                  </div>
                </div>
              </div>
            )}

            {formError && <p style={styles.formError}>{formError}</p>}

            <div style={styles.formButtons}>
              <button
                type="submit"
                disabled={creating}
                style={{
                  ...styles.submitBtn,
                  ...(creating ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                }}
              >
                {creating ? 'Making it...' : 'Make this coupon'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={styles.cancelBtn}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- The list ---------- */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>Who it is for</th>
              <th style={styles.th}>What they get</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Used</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((coupon) => {
              const status = statusOf(coupon);
              return (
                <tr key={coupon.id} style={styles.row}>
                  <td style={styles.cell}>
                    <span style={styles.codeText}>{coupon.code}</span>
                  </td>
                  <td style={{ ...styles.cell, color: '#333', fontSize: 13, maxWidth: 200 }}>
                    {coupon.note || <span style={{ color: '#9AA5B1' }}>Not noted</span>}
                  </td>
                  <td style={styles.cell}>
                    <span style={styles.productLabel}>
                      {productInfo(coupon.product_type).short}
                    </span>
                  </td>
                  <td style={styles.cell}>
                    <span style={{ ...styles.badge, backgroundColor: status.bg, color: status.fg }}>
                      {status.label}
                    </span>
                  </td>
                  <td style={{ ...styles.cell, whiteSpace: 'nowrap' }}>
                    {coupon.current_uses}{coupon.max_uses ? ' of ' + coupon.max_uses : ' so far'}
                  </td>
                  <td style={{ ...styles.cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => copyText(
                        buildInstructions(coupon.code, coupon.product_type),
                        'row-' + coupon.id
                      )}
                      style={styles.primaryActionBtn}
                    >
                      {copied === 'row-' + coupon.id ? 'Copied' : 'Copy instructions'}
                    </button>
                    <button onClick={() => handleToggle(coupon)} style={styles.actionBtn}>
                      {coupon.is_active ? 'Turn off' : 'Turn on'}
                    </button>
                    <button onClick={() => handleDelete(coupon)} style={styles.deleteBtn}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {coupons.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...styles.cell, textAlign: 'center', color: '#6B7A8F' }}>
                  No coupon codes yet. Click "+ Make a coupon" to build your first one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.infoCard}>
        <h3 style={styles.infoTitle}>How this page works</h3>
        <ol style={styles.infoList}>
          <li>Click <strong>+ Make a coupon</strong>, answer the two questions, then click <strong>Make this coupon</strong>.</li>
          <li>Click <strong>Copy the whole message</strong> and paste it into an email or a text to the person.</li>
          <li>They follow those steps and their account opens with full access, at no charge.</li>
        </ol>
        <p style={styles.infoFooter}>
          To send the same message again later, click <strong>Copy instructions</strong> on any row in
          the list. Use <strong>Turn off</strong> to pause a code and keep its history, and
          {' '}<strong>Delete</strong> to remove it for good.
        </p>
      </div>
    </div>
  );
}

const styles = {
  title: { fontSize: 24, fontWeight: 700, color: '#0B3D2E', margin: 0 },
  subtitle: { fontSize: 14, color: '#6B7A8F', margin: '4px 0 0 0' },
  loading: { color: '#6B7A8F', fontSize: 15 },
  error: { color: '#D32F2F', fontSize: 15 },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    gap: 16,
    flexWrap: 'wrap',
  },
  createBtn: {
    background: '#2A9D8F',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  successCard: {
    background: '#F3FBF8',
    borderRadius: 10,
    padding: 24,
    border: '2px solid #2A9D8F',
    marginBottom: 24,
  },
  successHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  successTitle: { fontSize: 18, fontWeight: 700, color: '#0B3D2E', margin: 0 },
  closeBtn: {
    background: 'transparent',
    border: '1px solid #B7D6CD',
    color: '#0B3D2E',
    padding: '6px 14px',
    borderRadius: 4,
    fontSize: 13,
    cursor: 'pointer',
  },
  bigCodeRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  bigCode: {
    fontFamily: "'Courier New', monospace",
    fontWeight: 700,
    fontSize: 28,
    letterSpacing: 2,
    color: '#0B3D2E',
    background: 'white',
    border: '1px solid #D5E7E1',
    borderRadius: 6,
    padding: '10px 18px',
  },
  copyBtnSmall: {
    background: 'white',
    border: '1px solid #2A9D8F',
    color: '#0B3D2E',
    padding: '10px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  successSummary: { fontSize: 14, color: '#333', margin: '0 0 20px 0' },
  stepTwo: { borderTop: '1px solid #D5E7E1', paddingTop: 18 },
  stepTwoTitle: { fontSize: 15, fontWeight: 700, color: '#0B3D2E', margin: '0 0 12px 0' },
  copyBtnBig: {
    background: '#2A9D8F',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 14,
  },
  messageBox: {
    background: 'white',
    border: '1px solid #D5E7E1',
    borderRadius: 6,
    padding: 18,
    fontSize: 14,
    lineHeight: 1.7,
    color: '#333',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
    margin: 0,
  },

  formCard: {
    background: 'white',
    borderRadius: 10,
    padding: 24,
    border: '1px solid #E8ECF0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: 24,
  },
  formTitle: { fontSize: 18, fontWeight: 700, color: '#0B3D2E', margin: '0 0 20px 0' },
  step: { marginBottom: 22, maxWidth: 560 },
  formRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: 220 },
  label: { display: 'block', fontSize: 15, fontWeight: 700, color: '#0B3D2E', marginBottom: 8 },
  hint: { fontSize: 13, color: '#6B7A8F', margin: '8px 0 0 0', lineHeight: 1.5 },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #D0D5DD',
    borderRadius: 6,
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'white',
  },
  codeInput: {
    fontFamily: "'Courier New', monospace",
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: 1,
  },
  codeDisplay: {
    fontFamily: "'Courier New', monospace",
    fontWeight: 700,
    fontSize: 22,
    letterSpacing: 2,
    color: '#0B3D2E',
    background: '#F7F9FA',
    border: '1px solid #D0D5DD',
    borderRadius: 6,
    padding: '12px 16px',
  },
  codeActions: { display: 'flex', gap: 18, marginTop: 8, flexWrap: 'wrap' },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: '#2A9D8F',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  advancedToggle: {
    background: 'transparent',
    border: 'none',
    color: '#6B7A8F',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
    marginBottom: 14,
  },
  advancedBox: {
    background: '#F7F9FA',
    border: '1px solid #E8ECF0',
    borderRadius: 8,
    padding: 18,
    marginBottom: 18,
  },
  formError: { color: '#D32F2F', fontSize: 14, margin: '0 0 12px 0', fontWeight: 600 },
  formButtons: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 },
  submitBtn: {
    background: '#2A9D8F',
    color: 'white',
    border: 'none',
    padding: '12px 28px',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'transparent',
    border: '1px solid #D0D5DD',
    color: '#6B7A8F',
    padding: '12px 20px',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer',
  },

  tableWrapper: {
    background: 'white',
    borderRadius: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    overflow: 'auto',
    marginBottom: 24,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 12,
    fontWeight: 600,
    color: '#6B7A8F',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '2px solid #E8ECF0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #F0F2F5' },
  cell: { padding: '12px 16px', verticalAlign: 'middle' },
  codeText: {
    fontFamily: "'Courier New', monospace",
    fontWeight: 700,
    color: '#0B3D2E',
    fontSize: 14,
  },
  productLabel: { fontSize: 13, fontWeight: 600, color: '#2A9D8F' },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  primaryActionBtn: {
    background: '#E8F5F2',
    border: '1px solid #2A9D8F',
    padding: '6px 12px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: 8,
    color: '#0B3D2E',
  },
  actionBtn: {
    background: 'transparent',
    border: '1px solid #D0D5DD',
    padding: '6px 12px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    marginRight: 8,
    color: '#333',
  },
  deleteBtn: {
    background: 'transparent',
    border: '1px solid #FFCDD2',
    padding: '6px 12px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    color: '#D32F2F',
  },
  infoCard: {
    background: 'white',
    borderRadius: 10,
    padding: 24,
    border: '1px solid #E8ECF0',
  },
  infoTitle: { fontSize: 15, fontWeight: 700, color: '#0B3D2E', margin: '0 0 12px 0' },
  infoList: { margin: 0, paddingLeft: 20, color: '#333', lineHeight: 2, fontSize: 14 },
  infoFooter: { fontSize: 13, color: '#6B7A8F', margin: '14px 0 0 0', lineHeight: 1.7 },
};

export default AdminCoupons;
